/**
 * The fleet roster, grouped by team, with every other team's real aircraft in
 * a view-only section of their own.
 *
 * Other teams' aircraft are on the map so a unit sharing airspace has one
 * picture of what is in the air. They get no checkbox and no selection: the
 * server sends only their position and identity, refuses every command to
 * them, and a row that looked like the caller's own would invite exactly those
 * commands.
 *
 * Every row subscribes to the narrowest thing it renders. A battery readout
 * reads `batteryPercent` and nothing else, so a position fix does not wake it.
 * This matters more here than anywhere: the rail shows every aircraft at once,
 * and the predecessor's version destructured the whole store into one component
 * that re-rendered the entire list at the broadcast rate of the busiest drone.
 */

import { useEffect, useMemo, useState, type MouseEvent } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { api } from '@/api/client';
import { ConnectDialog } from '@/features/connections/ConnectDialog';
import { DroneCardReadouts, DroneKindPill } from '@/features/fleet/DroneKind';
import { UnidentifiedBadge } from '@/features/data/IdentifyDialog';
import { useAorScopedIds } from '@/features/fleet/aorScope';
import { TeamsPanel } from '@/features/fleet/TeamsPanel';
import { cn } from '@/lib/utils';
import { useCanCommand } from '@/stores/authStore';
import {
  useDroneIdentity,
  useDroneMessage,
  useDroneScalars,
  useDroneSelection,
  useDroneStore,
  useIsSelected,
  useSelectedDroneIds,
} from '@/stores/droneStore';
import type { DroneId } from '@/types/domain';

interface DroneTeam {
  teamId: string;
  callsign: string;
  color: string;
}

type DroneTeamMap = Record<DroneId, DroneTeam>;

let teamMapCache: DroneTeamMap = {};
const teamMapListeners = new Set<(value: DroneTeamMap) => void>();

/**
 * Which team owns which drone.
 *
 * `/api/teams/drone-map` is the only endpoint that answers this and it is
 * already scoped to what the caller may see, so the rail never has to filter
 * the answer. Cached at module level because the map screen wants the same
 * colours and neither should be fetching it twice.
 */
export function useDroneTeams(): DroneTeamMap {
  const [teams, setTeams] = useState<DroneTeamMap>(teamMapCache);

  useEffect(() => {
    teamMapListeners.add(setTeams);
    void (async () => {
      const result = await api.get<{ droneMap: DroneTeamMap }>('/teams/drone-map', {
        logged: false,
      });
      if (!result.ok) return;
      teamMapCache = result.data.droneMap ?? {};
      for (const listener of teamMapListeners) listener(teamMapCache);
    })();
    return () => {
      teamMapListeners.delete(setTeams);
    };
  }, []);

  return teams;
}

export function teamColorFor(droneId: DroneId): string | null {
  return teamMapCache[droneId]?.color ?? null;
}

/**
 * Link quality from what MAVLink actually reports.
 *
 * RC_CHANNELS.rssi is 0 to 254 with 255 meaning "no reading", which is the
 * quirk that matters: treating 255 as a value shows a perfect link on an
 * aircraft that is not reporting one. SYS_STATUS.drop_rate_comm is in
 * hundredths of a percent, not percent, so a raw 250 is 2.5 percent lost and
 * not a quarter of the traffic.
 */
function linkQuality(
  rc: Record<string, unknown> | undefined,
  sys: Record<string, unknown> | undefined,
): { percent: number | null; dropPercent: number | null } {
  const rssi = typeof rc?.rssi === 'number' ? rc.rssi : null;
  const drop = typeof sys?.drop_rate_comm === 'number' ? sys.drop_rate_comm / 100 : null;
  const percent = rssi === null || rssi === 255 ? null : Math.round((rssi / 254) * 100);
  return { percent, dropPercent: drop };
}

/** Affiliation quad — own UAS render friend (2525). A small glass-outlined marker glyph. */
function AffiliationQuad({ color, stale }: { color: string; stale: boolean }) {
  return (
    <svg width="15" height="15" viewBox="0 0 16 16" aria-hidden="true" className="shrink-0">
      <path
        d="M8 2 L14 8 L8 14 L2 8 Z"
        fill="none"
        stroke={color}
        strokeWidth="1.6"
        opacity={stale ? 0.4 : 1}
      />
      <circle cx="8" cy="8" r="1.5" fill={color} opacity={stale ? 0.4 : 1} />
    </svg>
  );
}

function BatteryBar({ percent }: { percent: number | undefined }) {
  const value = percent ?? 0;
  const color =
    percent === undefined
      ? 'var(--muted-foreground)'
      : value <= 15
        ? 'var(--danger)'
        : value <= 30
          ? 'var(--caution)'
          : 'var(--ok)';
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="relative h-[5px] w-9 overflow-hidden rounded-full bg-[oklch(1_0_0/0.1)]">
        <span
          className="absolute inset-y-0 left-0 rounded-full"
          style={{ width: `${Math.max(0, Math.min(100, value))}%`, background: color }}
        />
      </span>
      <span className="tnum text-[10.5px]" style={{ color }}>
        {percent === undefined ? '--' : `${value.toFixed(0)}%`}
      </span>
    </span>
  );
}

function DroneRow({ droneId }: { droneId: DroneId }) {
  const identity = useDroneIdentity(droneId);
  const scalars = useDroneScalars(droneId, [
    'armed',
    'batteryPercent',
    'gpsFixType',
    'gpsSatellites',
  ]);
  const rc = useDroneMessage(droneId, 'RC_CHANNELS');
  const sys = useDroneMessage(droneId, 'SYS_STATUS');
  const selected = useIsSelected(droneId);
  const { select, toggleSelect } = useDroneSelection();

  if (!identity) return null;

  const stale = identity.state === 'stale' || identity.state === 'disconnected';
  const { percent, dropPercent } = linkQuality(rc, sys);
  const gpsOk = (scalars.gpsFixType ?? 0) >= 3;
  const linkColor = stale
    ? 'var(--danger)'
    : percent === null
      ? 'var(--muted-foreground)'
      : percent < 40
        ? 'var(--caution)'
        : 'var(--ok)';

  const onClick = (event: MouseEvent) => {
    // Modifier-click builds a selection for a fan-out command. A plain click
    // replaces it, so an operator cannot accidentally accumulate aircraft and
    // then arm all of them.
    if (event.metaKey || event.ctrlKey || event.shiftKey) toggleSelect(droneId);
    else select(droneId);
  };

  return (
    <div
      className={cn(
        'group flex items-stretch gap-2 rounded-[12px] border px-2 py-2 transition-colors',
        selected
          ? 'border-[color:var(--friend)] bg-[oklch(1_0_0/0.06)]'
          : 'border-transparent hover:bg-[oklch(1_0_0/0.04)]',
      )}
    >
      {/* A phone has no modifier key, so this box is the only way to build a
          multi-aircraft selection there; the label gives it a 44px target. */}
      <label className="hit-44 -m-1 flex shrink-0 items-start p-1">
        <input
          type="checkbox"
          checked={selected}
          onChange={() => toggleSelect(droneId)}
          aria-label={`Select ${identity.callsign} for a fan-out command`}
          className="mt-0.5 size-[15px] shrink-0 accent-[color:var(--friend)] pointer-coarse:size-[18px]"
        />
      </label>

      <button type="button" onClick={onClick} aria-pressed={selected} className="min-w-0 flex-1 text-left">
        <div className="flex items-center gap-2">
          <AffiliationQuad color="var(--friend)" stale={stale} />
          <span className="tnum min-w-0 truncate text-[13px] font-medium" title={identity.callsign}>
            {identity.callsign}
          </span>
          <DroneKindPill droneId={droneId} />
          {scalars.armed ? (
            <span
              className="rounded-[99px] px-1.5 py-px label-mono text-[8px]"
              style={{
                color: 'var(--armed)',
                background: 'color-mix(in oklch, var(--armed) 16%, transparent)',
              }}
            >
              ARMED
            </span>
          ) : null}
          {identity.simulated ? (
            <span
              className="rounded-[99px] px-1.5 py-px label-mono text-[8px]"
              style={{
                color: 'var(--caution)',
                background: 'color-mix(in oklch, var(--caution) 14%, transparent)',
              }}
            >
              SIM
            </span>
          ) : null}
          {stale ? (
            <span
              className="rounded-[99px] px-1.5 py-px label-mono text-[8px]"
              style={{
                color: 'var(--danger-foreground)',
                background: 'color-mix(in oklch, var(--danger) 16%, transparent)',
              }}
            >
              {identity.state.toUpperCase()}
            </span>
          ) : null}
          <span className="ml-auto tnum text-[10px]" style={{ color: linkColor }}>
            {percent === null ? '--' : `${percent}%`}
          </span>
        </div>

        <div className="mt-1.5 flex items-center gap-3 pl-[23px]">
          <BatteryBar percent={scalars.batteryPercent} />
          <DroneCardReadouts droneId={droneId} />
          <span
            className="tnum ml-auto text-[10px]"
            style={{ color: gpsOk ? 'var(--muted-foreground)' : 'var(--caution)' }}
          >
            {gpsOk ? `GPS ${scalars.gpsSatellites ?? 0}` : 'NO FIX'}
          </span>
          {dropPercent !== null && dropPercent > 0 ? (
            <span className="tnum text-[10px]" style={{ color: 'var(--caution)' }}>
              ▼{dropPercent.toFixed(1)}%
            </span>
          ) : null}
        </div>
      </button>
      <UnidentifiedBadge droneId={droneId} />
    </div>
  );
}

/**
 * Another team's aircraft: who it is, whose it is, where and whether it is
 * armed. Everything else about it belongs to that team, so nothing else is
 * shown and nothing here can be clicked into a command.
 */
function ViewOnlyRow({ droneId, team }: { droneId: DroneId; team: DroneTeam | undefined }) {
  const identity = useDroneIdentity(droneId);
  const scalars = useDroneScalars(droneId, ['armed', 'altRelative', 'groundSpeed']);
  if (!identity) return null;
  const stale = identity.state === 'stale' || identity.state === 'disconnected';
  const color = team?.color ?? 'var(--muted-foreground)';

  return (
    <div
      className="flex items-center gap-2 rounded-[12px] px-2 py-1.5"
      title={`${team?.callsign ?? 'Another team'}'s aircraft. Its details and commands stay with that team.`}
      data-testid={`view-only-${droneId}`}
    >
      <AffiliationQuad color={color} stale={stale} />
      <span className="tnum min-w-0 truncate text-[12.5px]" title={identity.callsign}>
        {identity.callsign}
      </span>
      {scalars.armed ? (
        <span
          className="rounded-[99px] px-1.5 py-px label-mono text-[8px]"
          style={{
            color: 'var(--armed)',
            background: 'color-mix(in oklch, var(--armed) 16%, transparent)',
          }}
        >
          ARMED
        </span>
      ) : null}
      <span className="tnum ml-auto text-[10.5px] text-[color:var(--muted-foreground)]">
        {scalars.altRelative === undefined ? 'ALT --' : `ALT ${scalars.altRelative.toFixed(0)} m`}
      </span>
      <span className="tnum text-[10.5px] text-[color:var(--muted-foreground)]">
        {scalars.groundSpeed === undefined ? 'GS --' : `GS ${scalars.groundSpeed.toFixed(1)}`}
      </span>
    </div>
  );
}

/** Ids the server sent at map level only: other teams' real aircraft. */
function useMapOnlyIds(): Set<DroneId> {
  const ids = useDroneStore(
    useShallow((state) => state.ids.filter((id) => state.drones.get(id)?.access === 'map')),
  );
  return useMemo(() => new Set(ids), [ids]);
}

export function FleetRail() {
  const allIds = useAorScopedIds();
  const mapOnly = useMapOnlyIds();
  const ids = useMemo(() => allIds.filter((id) => !mapOnly.has(id)), [allIds, mapOnly]);
  const theirs = useMemo(() => allIds.filter((id) => mapOnly.has(id)), [allIds, mapOnly]);
  const teams = useDroneTeams();
  const selectedIds = useSelectedDroneIds();
  const { clearSelection } = useDroneSelection();
  const canCommand = useCanCommand();
  const [query, setQuery] = useState('');
  const [connectOpen, setConnectOpen] = useState(false);

  const groups = useMemo(() => {
    const filter = query.trim().toLowerCase();
    const byTeam = new Map<string, { callsign: string; color: string; ids: DroneId[] }>();

    for (const droneId of ids) {
      if (filter && !droneId.toLowerCase().includes(filter)) continue;
      const team = teams[droneId];
      const key = team?.teamId ?? '';
      const group = byTeam.get(key) ?? {
        // Aircraft nobody has claimed still have to appear. In the predecessor
        // they were filtered out of a team-grouped list entirely, so a drone
        // that connected before it was assigned was invisible.
        callsign: team?.callsign ?? 'Unassigned',
        color: team?.color ?? 'var(--muted-foreground)',
        ids: [],
      };
      group.ids.push(droneId);
      byTeam.set(key, group);
    }

    return [...byTeam.entries()]
      .sort(([a], [b]) => (a === '' ? 1 : b === '' ? -1 : a.localeCompare(b)))
      .map(([teamId, group]) => ({ teamId, ...group }));
  }, [ids, teams, query]);

  const others = useMemo(() => {
    const filter = query.trim().toLowerCase();
    return theirs
      .filter((droneId) => !filter || droneId.toLowerCase().includes(filter))
      .sort((a, b) =>
        (teams[a]?.callsign ?? '').localeCompare(teams[b]?.callsign ?? '') || a.localeCompare(b),
      );
  }, [theirs, teams, query]);

  return (
    <div className="flex h-full flex-col">
      <div className="flex h-12 shrink-0 items-center gap-2 px-4">
        <span className="label-mono text-[10px]">FLEET</span>
        <span className="tnum text-[11px] text-[color:var(--muted-foreground)]">{ids.length}</span>
        {selectedIds.length > 1 ? (
          <button
            type="button"
            onClick={clearSelection}
            className="rounded-[99px] border border-[color:var(--border)] px-2 py-0.5 label-mono text-[8.5px] text-[color:var(--muted-foreground-3)] hover:text-foreground"
          >
            {selectedIds.length} SELECTED · CLEAR
          </button>
        ) : null}
        {canCommand ? (
          <button
            type="button"
            onClick={() => setConnectOpen(true)}
            title="Connect a drone or radio"
            className="ml-auto rounded-[99px] border border-[color:var(--border)] bg-white/5 px-2.5 py-0.5 label-mono text-[8.5px] text-foreground hover:bg-white/10"
          >
            + CONNECT
          </button>
        ) : null}
      </div>

      {connectOpen ? <ConnectDialog onClose={() => setConnectOpen(false)} /> : null}

      <div className="shrink-0 px-3 pb-2">
        <input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Filter aircraft…"
          aria-label="Filter fleet"
          className="well h-8 w-full rounded-[99px] px-4 text-[12px] outline-none placeholder:text-[color:var(--muted-foreground)]"
        />
      </div>

      <div className="shrink-0 px-2 pb-2">
        <TeamsPanel droneTeams={teams} />
      </div>

      <div className="scroll-thin min-h-0 flex-1 overflow-y-auto px-2 pb-2">
        {ids.length === 0 && theirs.length === 0 ? (
          <p className="px-3 py-4 text-[11px] text-[color:var(--muted-foreground)]">
            No aircraft connected. Start the simulator or bring a link up; drones appear here on
            their first heartbeat.
          </p>
        ) : (
          groups.map((group) => (
            <section key={group.teamId || 'unassigned'} className="mb-1">
              <header className="flex items-center gap-2 px-2 py-1.5">
                <span
                  className="size-2 rounded-full"
                  style={{ backgroundColor: group.color }}
                  aria-hidden
                />
                <span className="label-mono text-[9px] text-[color:var(--muted-foreground)]">
                  {group.callsign.toUpperCase()}
                </span>
                <span className="tnum ml-auto text-[10px] text-[color:var(--muted-foreground-2)]">
                  {group.ids.length}
                </span>
              </header>
              <div className="flex flex-col gap-0.5">
                {group.ids.map((droneId) => (
                  <DroneRow key={droneId} droneId={droneId} />
                ))}
              </div>
            </section>
          ))
        )}

        {others.length ? (
          <section className="mt-2 border-t border-[color:var(--border-nested)] pt-1.5">
            <header className="flex items-center gap-2 px-2 py-1.5">
              <span className="label-mono text-[9px] text-[color:var(--muted-foreground)]">
                OTHER TEAMS
              </span>
              <span className="label-mono text-[8px] text-[color:var(--muted-foreground-2)]">
                VIEW ONLY
              </span>
              <span className="tnum ml-auto text-[10px] text-[color:var(--muted-foreground-2)]">
                {others.length}
              </span>
            </header>
            <div className="flex flex-col">
              {others.map((droneId) => (
                <ViewOnlyRow key={droneId} droneId={droneId} team={teams[droneId]} />
              ))}
            </div>
          </section>
        ) : null}
      </div>
    </div>
  );
}

export default FleetRail;
